import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useAppSelector } from '../../store/hooks';
import './AdminComponents.css';

interface MultiSignatureWallet {
  id: number;
  walletAddress: string;
  blockchain: string;
  contractAddress?: string;
  requiredSignatures: number;
  totalSignatories: number;
  walletType: string;
  status: string;
  balance: number;
  currency: string;
  propertyAssetId?: number;
  escrowTerms?: string;
  releaseConditions?: string;
  createdAt: string;
  activatedAt?: string;
  closedAt?: string;
  propertyAsset?: {
    tokenSymbol: string;
    assetName: string;
    assetValue: number;
    assetLocation?: string;
    certificateNumber?: string;
    status?: string;
    description?: string;
    legalDocumentationLinks?: string[];
  };
  signatories?: MultiSignatureSignatory[];
  pendingTransactions?: MultiSignatureTransaction[];
}

interface MultiSignatureSignatory {
  id: number;
  userId: number;
  username: string;
  signatoryType: string;
  publicKey: string;
  signatoryAddress?: string;
  signatureWeight: number;
  isActive: boolean;
  addedAt: string;
  lastSignatureAt?: string;
}

interface MultiSignatureTransaction {
  id: number;
  transactionHash?: string;
  transactionType: string;
  amount: number;
  currency: string;
  recipientAddress?: string;
  description?: string;
  status: string;
  requiredSignatures: number;
  receivedSignatures: number;
  createdAt: string;
  expiresAt: string;
  executedAt?: string;
  approvals?: MultiSignatureApproval[];
}

interface MultiSignatureApproval {
  id: number;
  signatoryId: number;
  signatoryUsername: string;
  signatoryType: string;
  approvalType: string;
  signatureHash?: string;
  approvalNote?: string;
  approvedAt: string;
}

const MultiSignatureWallets: React.FC = () => {
  const [wallets, setWallets] = useState<MultiSignatureWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<MultiSignatureWallet | null>(null);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<MultiSignatureTransaction | null>(null);
  const [transactionFormData, setTransactionFormData] = useState({
    transactionType: 'TRANSFER',
    amount: '',
    currency: 'USD',
    recipientAddress: '',
    description: '',
    expirationDays: 7
  });
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);

  const { getMultiSignatureWallets, createWalletTransaction, activateMultiSignatureWallet, freezeMultiSignatureWallet } = useAdminApiService();
  const adminToken = useAppSelector(state => state.auth.token);

  useEffect(() => {
    loadWallets();
  }, []);

  const loadWallets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMultiSignatureWallets();
      if (response.success) {
        setWallets(response.data || []);
      } else {
        setError(response.error || 'Failed to load multi-signature wallets');
      }
    } catch (err) {
      console.error('Error loading wallets:', err);
      setError('Failed to load multi-signature wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewWalletDetails = async (wallet: MultiSignatureWallet) => {
    setSelectedWallet(wallet);
    setShowWalletDetails(true);
  };

  const handleCreateTransaction = async () => {
    if (!selectedWallet) return;

    try {
      setIsCreatingTransaction(true);
      
      const response = await createWalletTransaction(selectedWallet.id, transactionFormData);

      if (response.success) {
        alert('Transaction created successfully!');
        setShowTransactionModal(false);
        setTransactionFormData({
          transactionType: 'TRANSFER',
          amount: '',
          currency: 'USD',
          recipientAddress: '',
          description: '',
          expirationDays: 7
        });
        // Refresh wallet details
        await loadWallets();
      } else {
        setError(response.error || 'Failed to create transaction');
      }
    } catch (err) {
      setError('Failed to create transaction');
      console.error('Error creating transaction:', err);
    } finally {
      setIsCreatingTransaction(false);
    }
  };

  const handleActivateWallet = async (walletId: number) => {
    try {
      const response = await activateMultiSignatureWallet(walletId);
      if (response.success) {
        await loadWallets();
      } else {
        setError(response.error || 'Failed to activate wallet');
      }
    } catch (err) {
      setError('Failed to activate wallet');
    }
  };

  const handleFreezeWallet = async (walletId: number) => {
    try {
      const response = await freezeMultiSignatureWallet(walletId);
      if (response.success) {
        await loadWallets();
      } else {
        setError(response.error || 'Failed to freeze wallet');
      }
    } catch (err) {
      setError('Failed to freeze wallet');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'PENDING': return '#ffc107';
      case 'FROZEN': return '#dc3545';
      case 'CLOSED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#ffc107';
      case 'APPROVED': return '#28a745';
      case 'EXECUTED': return '#007bff';
      case 'EXPIRED': return '#6c757d';
      case 'CANCELLED': return '#dc3545';
      case 'FAILED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading multi-signature wallets...</p>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {error && (
        <div className="admin-error-message">
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button 
          onClick={loadWallets}
          className="admin-button"
        >
          🔄 Refresh
        </button>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Wallet Address</th>
              <th>Type</th>
              <th>Blockchain</th>
              <th>Status</th>
              <th>Balance</th>
              <th>Signatures</th>
              <th>Property Asset</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {wallets.map((wallet) => (
              <tr key={wallet.id}>
                <td>
                  <code style={{ fontSize: '12px' }}>{wallet.walletAddress.substring(0, 20)}...</code>
                </td>
                <td>
                  <span className="asset-type-badge" style={{ 
                    backgroundColor: wallet.walletType === 'PROPERTY_ESCROW' ? '#4ecdc4' : '#6c757d',
                    color: '#ffffff',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    display: 'inline-block',
                    textTransform: 'uppercase'
                  }}>
                    {wallet.walletType}
                  </span>
                </td>
                <td>{wallet.blockchain}</td>
                <td>
                  <span style={{ 
                    color: getStatusColor(wallet.status),
                    fontWeight: '600'
                  }}>
                    {wallet.status}
                  </span>
                </td>
                <td>{wallet.balance} {wallet.currency}</td>
                <td>{wallet.requiredSignatures}/{wallet.totalSignatories}</td>
                <td>
                  {wallet.propertyAsset ? (
                    <div>
                      <div><strong>{wallet.propertyAsset.tokenSymbol}</strong></div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {wallet.propertyAsset.assetName}
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>N/A</span>
                  )}
                </td>
                <td>{new Date(wallet.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    onClick={() => handleViewWalletDetails(wallet)}
                    className="admin-button"
                  >
                    👁️ View
                  </button>
                  {wallet.status === 'PENDING' && (
                    <button
                      onClick={() => handleActivateWallet(wallet.id)}
                      className="admin-button success"
                      style={{ marginLeft: '8px' }}
                    >
                      ✅ Activate
                    </button>
                  )}
                  {wallet.status === 'ACTIVE' && (
                    <button
                      onClick={() => handleFreezeWallet(wallet.id)}
                      className="admin-button warning"
                      style={{ marginLeft: '8px' }}
                    >
                      ❄️ Freeze
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Wallet Details Modal */}
      {showWalletDetails && selectedWallet && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px' }}>
            <div className="user-details-modal">
              <div className="modal-header">
                <h3>Multi-Signature Wallet Details</h3>
                <button 
                  className="modal-close" 
                  onClick={() => setShowWalletDetails(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <h4>Wallet Information</h4>
                    <div><strong>Address:</strong> <code>{selectedWallet.walletAddress}</code></div>
                    <div><strong>Blockchain:</strong> {selectedWallet.blockchain}</div>
                    <div><strong>Type:</strong> {selectedWallet.walletType}</div>
                    <div><strong>Status:</strong> 
                      <span style={{ color: getStatusColor(selectedWallet.status), fontWeight: '600' }}>
                        {selectedWallet.status}
                      </span>
                    </div>
                    <div><strong>Balance:</strong> {selectedWallet.balance} {selectedWallet.currency}</div>
                    <div><strong>Required Signatures:</strong> {selectedWallet.requiredSignatures}</div>
                    <div><strong>Total Signatories:</strong> {selectedWallet.totalSignatories}</div>
                  </div>
                  
                  <div>
                    <h4>Property Asset</h4>
                    {selectedWallet.propertyAsset ? (
                      <div>
                        <div><strong>Token:</strong> {selectedWallet.propertyAsset.tokenSymbol}</div>
                        <div><strong>Asset:</strong> {selectedWallet.propertyAsset.assetName}</div>
                        <div><strong>Value:</strong> ${selectedWallet.propertyAsset.assetValue?.toLocaleString()}</div>
                        <div><strong>Location:</strong> {selectedWallet.propertyAsset.assetLocation || 'N/A'}</div>
                        <div><strong>Certificate:</strong> {selectedWallet.propertyAsset.certificateNumber || 'N/A'}</div>
                        <div><strong>Status:</strong> 
                          <span style={{ 
                            color: selectedWallet.propertyAsset.status === 'ACTIVE' ? '#28a745' : '#dc3545',
                            fontWeight: '600'
                          }}>
                            {selectedWallet.propertyAsset.status}
                          </span>
                        </div>
                        {selectedWallet.propertyAsset.description && (
                          <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
                            <div><strong>Description:</strong></div>
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                              {selectedWallet.propertyAsset.description}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>No property asset associated</div>
                    )}
                  </div>
                </div>

                {/* Ownership & Stakeholders Section */}
                {selectedWallet.signatories && selectedWallet.signatories.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4>Ownership & Stakeholders</h4>
                    
                    {/* Ownership Summary */}
                    <div style={{ 
                      background: '#e3f2fd', 
                      padding: '16px', 
                      borderRadius: '8px', 
                      marginBottom: '16px',
                      border: '1px solid #2196f3'
                    }}>
                      <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>
                        📋 Ownership Summary
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px' }}>
                        <div><strong>Property Owner:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.username || 'Not assigned'}</div>
                        <div><strong>Buyer:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.username || 'Not assigned'}</div>
                        <div><strong>Admin:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.username || 'Not assigned'}</div>
                        <div><strong>Total Stakeholders:</strong> {selectedWallet.signatories.length}</div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                      {/* Property Owner */}
                      {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER') && (
                        <div style={{ 
                          background: '#f8f9fa', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '2px solid #28a745'
                        }}>
                          <div style={{ fontWeight: '600', color: '#28a745', marginBottom: '8px' }}>
                            🏠 Property Owner
                          </div>
                          <div><strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.username}</div>
                          <div><strong>Role:</strong> Property Owner</div>
                          <div><strong>Status:</strong> 
                            <span style={{ 
                              color: selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.isActive ? '#28a745' : '#dc3545',
                              fontWeight: '600'
                            }}>
                              {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Buyer */}
                      {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER') && (
                        <div style={{ 
                          background: '#f8f9fa', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '2px solid #007bff'
                        }}>
                          <div style={{ fontWeight: '600', color: '#007bff', marginBottom: '8px' }}>
                            👤 Buyer
                          </div>
                          <div><strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.username}</div>
                          <div><strong>Role:</strong> Buyer</div>
                          <div><strong>Status:</strong> 
                            <span style={{ 
                              color: selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.isActive ? '#28a745' : '#dc3545',
                              fontWeight: '600'
                            }}>
                              {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Admin */}
                      {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN') && (
                        <div style={{ 
                          background: '#f8f9fa', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '2px solid #6c757d'
                        }}>
                          <div style={{ fontWeight: '600', color: '#6c757d', marginBottom: '8px' }}>
                            ⚙️ Admin
                          </div>
                          <div><strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.username}</div>
                          <div><strong>Role:</strong> Administrator</div>
                          <div><strong>Status:</strong> 
                            <span style={{ 
                              color: selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.isActive ? '#28a745' : '#dc3545',
                              fontWeight: '600'
                            }}>
                              {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Other Signatories */}
                      {selectedWallet.signatories.filter(s => !['PROPERTY_OWNER', 'BUYER', 'ADMIN'].includes(s.signatoryType)).map((signatory) => (
                        <div key={signatory.id} style={{ 
                          background: '#f8f9fa', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '2px solid #ffc107'
                        }}>
                          <div style={{ fontWeight: '600', color: '#ffc107', marginBottom: '8px' }}>
                            👥 {signatory.signatoryType.replace('_', ' ')}
                          </div>
                          <div><strong>Name:</strong> {signatory.username}</div>
                          <div><strong>Role:</strong> {signatory.signatoryType.replace('_', ' ')}</div>
                          <div><strong>Status:</strong> 
                            <span style={{ 
                              color: signatory.isActive ? '#28a745' : '#dc3545',
                              fontWeight: '600'
                            }}>
                              {signatory.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Escrow Terms & Conditions */}
                {(selectedWallet.escrowTerms || selectedWallet.releaseConditions) && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4>Escrow Terms & Conditions</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      {selectedWallet.escrowTerms && (
                        <div style={{ 
                          background: '#fff3cd', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '1px solid #ffeaa7'
                        }}>
                          <div style={{ fontWeight: '600', color: '#856404', marginBottom: '8px' }}>
                            📋 Escrow Terms
                          </div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {selectedWallet.escrowTerms}
                          </div>
                        </div>
                      )}
                      
                      {selectedWallet.releaseConditions && (
                        <div style={{ 
                          background: '#d1ecf1', 
                          padding: '16px', 
                          borderRadius: '8px',
                          border: '1px solid #bee5eb'
                        }}>
                          <div style={{ fontWeight: '600', color: '#0c5460', marginBottom: '8px' }}>
                            🔓 Release Conditions
                          </div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {selectedWallet.releaseConditions}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedWallet.signatories && selectedWallet.signatories.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4>Signatories</h4>
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Type</th>
                            <th>Weight</th>
                            <th>Status</th>
                            <th>Last Signature</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedWallet.signatories.map((signatory) => (
                            <tr key={signatory.id}>
                              <td>{signatory.username}</td>
                              <td>{signatory.signatoryType}</td>
                              <td>{signatory.signatureWeight}</td>
                              <td>
                                <span style={{ 
                                  color: signatory.isActive ? '#28a745' : '#dc3545',
                                  fontWeight: '600'
                                }}>
                                  {signatory.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                {signatory.lastSignatureAt ? 
                                  new Date(signatory.lastSignatureAt).toLocaleDateString() : 
                                  'Never'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {selectedWallet.pendingTransactions && selectedWallet.pendingTransactions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4>Pending Transactions</h4>
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Signatures</th>
                            <th>Created</th>
                            <th>Expires</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedWallet.pendingTransactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td>{transaction.transactionType}</td>
                              <td>{transaction.amount} {transaction.currency}</td>
                              <td>
                                <span style={{ 
                                  color: getTransactionStatusColor(transaction.status),
                                  fontWeight: '600'
                                }}>
                                  {transaction.status}
                                </span>
                              </td>
                              <td>{transaction.receivedSignatures}/{transaction.requiredSignatures}</td>
                              <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                              <td>{new Date(transaction.expiresAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="modal-actions">
                  <button 
                    onClick={() => {
                      setShowTransactionModal(true);
                      setShowWalletDetails(false);
                    }}
                    className="admin-button success"
                    disabled={selectedWallet.status !== 'ACTIVE'}
                  >
                    ➕ Create Transaction
                  </button>
                  <button 
                    onClick={() => setShowWalletDetails(false)}
                    className="admin-button"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Transaction Modal */}
      {showTransactionModal && selectedWallet && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="user-details-modal">
              <div className="modal-header">
                <h3>Create Multi-Signature Transaction</h3>
                <button 
                  className="modal-close" 
                  onClick={() => setShowTransactionModal(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Transaction Type *</label>
                  <select 
                    value={transactionFormData.transactionType} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, transactionType: e.target.value }))}
                    className="form-select"
                  >
                    <option value="PROPERTY_PURCHASE">🏠 Property Purchase Payment</option>
                    <option value="ESCROW_RELEASE">🔓 Escrow Release</option>
                    <option value="REFUND">↩️ Refund</option>
                    <option value="FEE_PAYMENT">💰 Fee Payment</option>
                    <option value="WITHDRAWAL">💸 Withdrawal</option>
                    <option value="TRANSFER">💸 Transfer</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Amount *</label>
                  <input 
                    type="number" 
                    value={transactionFormData.amount} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="form-input"
                    placeholder="Enter amount"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Currency *</label>
                  <select 
                    value={transactionFormData.currency} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="form-select"
                  >
                    <option value="USD">USD</option>
                    <option value="ETH">ETH</option>
                    <option value="USDC">USDC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Recipient Address</label>
                  <input 
                    type="text" 
                    value={transactionFormData.recipientAddress} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, recipientAddress: e.target.value }))}
                    className="form-input"
                    placeholder="Enter recipient address"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    value={transactionFormData.description} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="form-input"
                    rows={3}
                    placeholder="Transaction description..."
                  />
                </div>

                <div className="form-group">
                  <label>Expiration (Days)</label>
                  <input 
                    type="number" 
                    value={transactionFormData.expirationDays} 
                    onChange={(e) => setTransactionFormData(prev => ({ ...prev, expirationDays: parseInt(e.target.value) }))}
                    className="form-input"
                    min="1"
                    max="30"
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    onClick={handleCreateTransaction}
                    className="admin-button success"
                    disabled={isCreatingTransaction}
                  >
                    {isCreatingTransaction ? 'Creating...' : 'Create Transaction'}
                  </button>
                  <button 
                    onClick={() => setShowTransactionModal(false)}
                    className="admin-button"
                    disabled={isCreatingTransaction}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSignatureWallets; 